import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { KeyRound, Smartphone } from "lucide-react";
import { Alert, Button, Field, Input, TabPanel, Tabs } from "@/components/ui";
import { useAuthStore } from "@/stores/auth-store";
import { ApiError, errorDetail } from "@/lib/api-client";
import { isSelectTenant, type LoginResponse, type LoginSuccess, type SelectTenantResponse } from "@/types/api";
import { useLogin, usePublicConfig, useSelectTenant, useSmsLogin } from "../hooks";
import { SmsCodeButton } from "../components/SmsCodeButton";
import { TenantPickerModal } from "../components/TenantPickerModal";
import { AuthCard, AuthShell } from "../components/AuthShell";
import { goLegacyHome, postLoginTarget } from "../utils";
import { LegacyHomeRedirect } from "@/components/layout/route-guards";

interface LocationState {
  from?: string;
}

type Tab = "password" | "sms";

const TAB_ITEMS = [
  { value: "password" as const, label: "账号密码", icon: KeyRound },
  { value: "sms" as const, label: "短信登录", icon: Smartphone },
];

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const config = usePublicConfig();
  const loginMutation = useLogin();
  const smsMutation = useSmsLogin();
  const selectMutation = useSelectTenant();

  const smsEnabled = !!config.data?.sms_login_enabled;
  const registerEnabled = !!config.data?.register_enabled;

  const [tab, setTab] = useState<Tab>("password");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // select_tenant: 票据一次性、5 分钟有效
  const [picker, setPicker] = useState<SelectTenantResponse | null>(null);
  const [pickingId, setPickingId] = useState<number | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);

  const from = (location.state as LocationState | null)?.from;

  /** 登录成功: 有深链回深链; 普通用户整页进旧版 /static/; 管理员进仪表盘 */
  const goAfterLogin = (role: LoginSuccess["user"]["role"]) => {
    const target = postLoginTarget(role, from);
    if (target === null) goLegacyHome();
    else navigate(target, { replace: true });
  };

  // 已登录 -> 按 role 重定向, 不停留在登录页
  // (正在整页跳旧版时也会走到这里: setSession 之后、页面卸载之前的那次重渲染)
  if (token && user) {
    const target = postLoginTarget(user.role, from);
    return target === null ? <LegacyHomeRedirect replace={false} /> : <Navigate to={target} replace />;
  }

  const activeTab: Tab = smsEnabled ? tab : "password";

  const handleResult = (data: LoginResponse) => {
    if (isSelectTenant(data)) {
      setPickError(null);
      setPicker(data);
      return;
    }
    goAfterLogin(data.user.role);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    try {
      if (activeTab === "password") {
        const u = username.trim();
        if (!u || !password) {
          setError("请输入用户名和密码");
          return;
        }
        handleResult(await loginMutation.mutateAsync({ username: u, password }));
      } else {
        const p = phone.trim();
        if (!p || !code.trim()) {
          setError("请输入手机号和验证码");
          return;
        }
        handleResult(await smsMutation.mutateAsync({ phone: p, code: code.trim() }));
      }
    } catch (err) {
      setError(errorDetail(err, activeTab === "password" ? "用户名或密码错误" : "登录失败"));
    }
  };

  const onPick = async (userId: number) => {
    if (!picker) return;
    setPickError(null);
    setPickingId(userId);
    try {
      const data = await selectMutation.mutateAsync({ ticket: picker.ticket, user_id: userId });
      setPicker(null);
      goAfterLogin(data.user.role);
    } catch (err) {
      // 票据过期 / 已用: 关掉弹窗让用户重新登录
      if (err instanceof ApiError && err.status === 401) {
        setPicker(null);
        setError(errorDetail(err, "登录已过期，请重新登录"));
      } else {
        setPickError(errorDetail(err, "选择失败"));
      }
    } finally {
      setPickingId(null);
    }
  };

  const pending = loginMutation.isPending || smsMutation.isPending;

  const fields =
    activeTab === "password" ? (
      <div className="space-y-4">
        <Field label="用户名">
          <Input
            id="username"
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="请输入用户名"
            className="h-10"
            disabled={pending}
          />
        </Field>
        <Field label="密码">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="请输入密码"
            className="h-10"
            disabled={pending}
          />
        </Field>
      </div>
    ) : (
      <div className="space-y-4">
        <Field label="手机号">
          <Input
            id="phone"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="请输入手机号"
            className="h-10"
            disabled={pending}
          />
        </Field>
        <div className="space-y-1.5">
          <label htmlFor="code" className="text-sm font-medium text-foreground">验证码</label>
          <div className="flex gap-2">
            <Input
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="6 位验证码"
              className="h-10 min-w-0 flex-1"
              disabled={pending}
            />
            <SmsCodeButton
              phone={phone}
              purpose="login"
              defaultInterval={config.data?.sms_resend_interval_seconds ?? 60}
              disabled={pending}
              onError={setError}
              onSent={setInfo}
            />
          </div>
        </div>
      </div>
    );

  return (
    <AuthShell>
      <AuthCard title="欢迎回来" subtitle="登录智能广告制作助手">
        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          {smsEnabled ? (
            <>
              <Tabs
                items={TAB_ITEMS}
                value={activeTab}
                onValueChange={(v) => {
                  setTab(v);
                  setError(null);
                  setInfo(null);
                }}
                idPrefix="login"
                aria-label="登录方式"
                className="-mx-1"
              />
              <TabPanel idPrefix="login" value={activeTab}>
                {fields}
              </TabPanel>
            </>
          ) : (
            fields
          )}

          {info && !error && <Alert variant="success">{info}</Alert>}
          {error && <Alert>{error}</Alert>}

          <Button type="submit" size="lg" loading={pending} className="w-full">
            {pending ? "登录中..." : "登录"}
          </Button>

          {registerEnabled && (
            <p className="text-center text-sm text-muted-foreground">
              还没有账号？
              <Link to="/register" className="ml-1 font-medium text-primary hover:underline">
                注册
              </Link>
            </p>
          )}
        </form>
      </AuthCard>

      {picker && (
        <TenantPickerModal
          choices={picker.choices}
          pendingUserId={pickingId}
          error={pickError}
          onPick={onPick}
          onClose={() => setPicker(null)}
        />
      )}
    </AuthShell>
  );
}
